import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, FileText, Landmark } from 'lucide-react';
import { DRHP_STATUS } from '../../utils/drhpTracker';
import { formatFilingDate } from '../../utils/upcomingIpos';
import CompanyLogo from '../common/CompanyLogo';

const BOARD_PILL = {
  sme: { label: 'SME IPO', className: 'border-amber-200 bg-amber-50 text-amber-700' },
  mainboard: { label: 'Mainboard IPO', className: 'border-indigo-200 bg-indigo-50 text-indigo-700' },
};

/** Issuer identity, where the filing stands and when each document arrived. */
export const FilingHero = ({ filing }) => {
  const board = BOARD_PILL[filing.board];
  const status = DRHP_STATUS[filing.regulatoryStatus];

  // The document's own date is the filing date; the tracker timestamp is only
  // when this site picked it up, so the two are labelled apart
  const dates = [
    { label: 'DRHP dated', value: filing.drhpDated },
    { label: 'RHP dated', value: filing.rhpDated },
    { label: 'Added to tracker', value: filing.drhpAddedOn ? formatFilingDate(filing.drhpAddedOn) : null },
    { label: 'RHP added', value: filing.rhpAddedOn ? formatFilingDate(filing.rhpAddedOn) : null },
  ].filter((entry) => entry.value);

  return (
    <div className="space-y-3">
      <nav aria-label="Breadcrumb" className="flex items-center gap-1.5 text-xs text-slate-500 font-medium overflow-x-auto whitespace-nowrap scrollbar-none">
        <Link to="/" className="hover:text-[#008080] transition-colors">
          Home
        </Link>
        <ChevronRight className="w-3.5 h-3.5 text-slate-400 shrink-0" />
        <Link to="/drhp-filings" className="hover:text-[#008080] transition-colors">
          DRHP Filings Tracker
        </Link>
        <ChevronRight className="w-3.5 h-3.5 text-slate-400 shrink-0" />
        <span className="text-slate-900 font-semibold" aria-current="page">
          {filing.company}
        </span>
      </nav>

      <section aria-labelledby="filing-heading" className="bg-white rounded-2xl border border-slate-200/90 shadow-sm p-4 sm:p-5">
        <div className="flex flex-col xl:flex-row xl:items-start justify-between gap-4">
          <div className="flex items-start gap-3.5 min-w-0">
            <CompanyLogo company={filing.company} logoUrl={filing.logoUrl} size="lg" />
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <h1 id="filing-heading" className="text-xl sm:text-2xl font-extrabold text-slate-900 tracking-tight leading-tight">
                  {filing.company}
                </h1>
                {board && <span className={`px-2 py-0.5 rounded-md border text-[11px] font-bold ${board.className}`}>{board.label}</span>}
              </div>
              <p className="mt-1.5 text-xs text-slate-500">
                {filing.cin ? `CIN ${filing.cin}` : 'CIN not stated in the filing'}
                {filing.parties.incorporatedOn && ` • Incorporated ${filing.parties.incorporatedOn}`}
              </p>
              {filing.products.length > 0 && (
                <ul className="mt-2 flex flex-wrap gap-1.5">
                  {filing.products.slice(0, 4).map((tag) => (
                    <li key={tag} className="px-2 py-0.5 rounded-md bg-slate-100 text-[11px] font-semibold text-slate-600 capitalize">
                      {tag}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          <div className="flex flex-wrap items-start gap-1.5 text-[11px] font-bold shrink-0">
            <span className={`px-2.5 py-1 rounded-full border ${status.badge}`}>{status.label}</span>
            <span className={`px-2.5 py-1 rounded-full border ${filing.pipeline.badge}`}>{filing.pipeline.label}</span>
          </div>
        </div>

        {dates.length > 0 && (
          <dl className="mt-4 pt-4 border-t border-slate-100 grid grid-cols-2 sm:grid-cols-4 gap-4">
            {dates.map((entry) => (
              <div key={entry.label}>
                <dt className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-400">
                  {entry.label === 'DRHP dated' || entry.label === 'RHP dated' ? (
                    <FileText className="w-3 h-3" aria-hidden="true" />
                  ) : (
                    <Landmark className="w-3 h-3" aria-hidden="true" />
                  )}
                  {entry.label}
                </dt>
                <dd className="mt-1 text-sm font-bold text-slate-900">{entry.value}</dd>
              </div>
            ))}
          </dl>
        )}

        {filing.pipeline.note && (
          <p className="mt-3 text-[11px] text-slate-500 leading-relaxed">{filing.pipeline.note}</p>
        )}
      </section>
    </div>
  );
};

export default FilingHero;
